/*
 * Copyright (c) 2022. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* basemodel.ts
* 8-7-2024
* Gestripte apilose versie van basemodel uit de grote applicaties
*
* tsconfig:
* strict: false
*
*/

import {cloneDeep, keyBy} from 'lodash-es';
import {hash} from "../types";
import {WPAIBase} from "../app/wpaibase";

/** modelfield decorator **/
export function ModelField(): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._modelFieldDecorator(key, descriptor);
  };
}

/**
 * Ownerfield decorator: adds a field that will be filled with the id of the owner, other side of a 1:n relation
 * @constructor
 */
export function OwnerField(): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._ownerFieldDecorator(key, descriptor);
  };
}

export interface modelrelation {
  type?: '1:n' | '1:1'; //type of relation. Default is 1:n. 1:1 betekent ook dat de FK in het kind zit, maar dat we dit afhandelen als max 1 per main
  model?: typeof Basemodel; //the related model
}

/**
 * Modelrelation decorator
 */
export function ModelRelation(definition: modelrelation = {}): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._modelRelationDecorator(key, descriptor, definition);
  };
}

export type modelrelations = hash<modelrelation>;

export class Basemodel extends WPAIBase {
  //static

  //internal properties of a  model
  protected static fields: string[] = []; //the fields we can get from / save to the backend database Use the @Modelfield decorator to set this
  protected static ownerfield: string | null = null; //the name of the ownerfield set with @Ownerfield. This field maps on the id of this._owner
  protected static relations: modelrelations = {}; //relation definitions. Use @ModelRelation to set this

  /**
   * standard properties of the relation definition
   * @private
   */
  private static defaultRelationProps = {
    type: '1:n', //The foreign key is in the foreign table, pointing back to the model's table
    model: <Basemodel><unknown>null
  };


  /**
   * Original data. Must be set, one way or another: by the owner when getting the data for this sub model
   * automatically set by setData of the owner, so also in the static that get or create data
   * @protected
   */
  protected _orig: hash<any> = {}; //de originele data volgens __setData(..., true);

  _tags: hash<any> = {}; //Space to add random data to a model instance, can be used to save ux data

  /**
   * the owner. For example: a function can be owned by a person
   */
  public _owner: Basemodel | null = null;

  //the only field we always define, because of usage in code
  id?: number | string;

  /**
   * Internal data for the trackedId getter
   * @private
   */
  private _trackerid: string = "";
  static trackerIdSerial: number = 0; //make sure every instance has it's unique id nuber


  /**
   * Implements @ModelField decorator
   */
  public static _modelFieldDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor) {
    //we just make a field (property) and save it the fields array that we defined at the level of specific classes

    if (!this.hasOwnProperty('fields')) {
      //copy the definition from basemodel
      Object.defineProperty(this, 'fields', <PropertyDescriptor> Object.getOwnPropertyDescriptor(Basemodel, 'fields'));
      this.fields = [];
    }
    this.fields.push(key);
    descriptor = descriptor || {};
    descriptor.enumerable = true;
    descriptor.writable = true;
    return descriptor;
  }

  /**
   * Implements @OwnerField decorator: adds a field for the id of the _owner
   * @param key
   * @param descriptor
   */
  public static _ownerFieldDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor) {
    if (!this.hasOwnProperty('ownerfield')) {
      //copy the definition from basemodel
      Object.defineProperty(this, 'ownerfield', <PropertyDescriptor> Object.getOwnPropertyDescriptor(Basemodel, 'ownerfield'));
      this.ownerfield = null;
    }
    this.ownerfield = key; //so code can use it. It is NOT part of fields
    descriptor = {
      get() {
        return (<InstanceType<Modelclass>> this)._owner?.id;
      },
      enumerable: true
    };
    return descriptor;
  }


  /**
   * Implements the @ModelRelation decorator
   */
  public static _modelRelationDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor, definition: modelrelation) {
    //save the field and add it to the relations array of the specific Basemodel subclass

    if (!this.hasOwnProperty('relations')) {
      //copy from basemodel
      Object.defineProperty(this, 'relations', <PropertyDescriptor> Object.getOwnPropertyDescriptor(Basemodel, 'relations'));
      this.relations = {};
    }
    this.relations[key] = definition;
    descriptor = descriptor || {};
    descriptor.enumerable = true;
    descriptor.writable = true;
    return descriptor;
  }

  /**
   * Statics for getting data and creating instances. Just using the constructor is not enough
   */

  /**
   * Create a new instance for use as a new record. Will set defaults and initialize the relations as empty arrays. Can initialize with given data.
   * @param owner Owner of the new instance
   * @param data Optional init data
   */
  static createNew<Modelclass extends typeof Basemodel>(this: Modelclass, owner: Basemodel | null = null, data:  hash<any> = {}): InstanceType<Modelclass> {
    let inst = new this as InstanceType<Modelclass>;
    //alle defaults are set now
    //we have to create the right relation structure
    for (let relkey of Object.keys(this.relations)) {
      inst[relkey] = inst.relationDetails(relkey).type === '1:n' ? [] : null;
    }

    //overwrite with given data
    if (data) {
      inst.setData(data, false); //don't do orig now, we do it below
    }

    //create orig: same data as the base
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = this[field];
    }
    //owner (and ownerfield, if set)
    inst._owner = owner;
    return inst;
  }

  /////// Create an instance from given (backend) data
  static create<Modelclass extends typeof Basemodel>(this: Modelclass, data: hash<any> = {}, owner: Basemodel = null): InstanceType<Modelclass> {
    let inst = new this as InstanceType<Modelclass>;
    inst.setData(data, true);
    //save the owner
    inst._owner = owner;
    return inst;
  }

  /**
   * Create multiple instance from given (backend) data
   * @param data
   * @param owner
   */
  static createAll<Modelclass extends typeof Basemodel>(this: Modelclass, data: hash<any>[], owner: Basemodel = null): InstanceType<Modelclass>[] {
    return data.map(rec => this.create(rec, owner));
  }

  /**
   * Maak een lookup hash van de gegeven data, met als key de waarde van het opgeven veld uit elk object (standaard: id), en als value het gegenereerde object
   * @param data
   * @param owner
   * @param key
   */

  static createLookup<Modelclass extends typeof Basemodel>(this: Modelclass, data: hash<any>[], owner: Basemodel = null, key: string = 'id'): hash<InstanceType<Modelclass>> {
    return keyBy(this.createAll(data, owner),key);
  }



  /////////////////////////////////// Constructor ///////////////////////////////
  constructor() {
    super();
  }

  //////////////////////// logic ////////////////////////////
  //publics are at the end

  /**
   * Helper for findng and setting static properties in the actual class. These must be properties that are defind in Basemodel.
   * @protected
   */
  protected get _actualClass(): typeof Basemodel {
    return this.constructor as typeof Basemodel;
  }

  /**
   * Get the fiels from the class props
   * @protected
   */
  protected get _fields(): string[] {
    return this._actualClass.fields;
  }

  protected get _ownerfield(): string | null {
    return this._actualClass.ownerfield;
  }

  protected get _relations(): modelrelations {
    return this._actualClass.relations;
  }

  /**
   * De fields for relations
   * @protected
   */
  protected get _relationFields(): string[] {
    return Object.keys(this._actualClass.relations);
  }

  /**
   * Combineer all data properties
   * @protected
   */
  protected get _fieldsAndRelations(): string[] {
    return [...this._fields, ...this._relationFields];
  }

  /**
   * Get the details for a relation
   * @param relationKey
   * @protected
   */
  protected relationDetails(relationKey: string): modelrelation {
    //defaults erin
    return Object.assign({}, Basemodel.defaultRelationProps, this._actualClass.relations[relationKey]);

  }

  //////////////////////// Publics ////////////////////////////

  /**
   * Set the data, only accepting defined fields and relations
   * @param data
   * @param resetOrig when true, also reset the _orig for all fields
   */
  public setData(data: hash<any>, resetOrig: boolean = false) {
    //velden
    for (let field of this._fields) {
      if (field in data) {
        this[field] = data[field];
      }
    }
    //relations
    for (let relkey of this._relationFields) {
      let rel = this.relationDetails(relkey);
      let reldata: hash<any> | hash<any>[] = data[relkey]; //geen tabeldata, moet gewoon hetzelfde zijn

      //1:1 relatie? Dan de eerste uit een array
      if (rel.type === '1:1' && Array.isArray(reldata))
      {
        reldata = reldata[0]; //kan undefined zijn
      }
      if (reldata) {
        //not null or undefined

        // this._log.debug(`Relatie`, rel);

        //create a model instance?
        if (rel.model) {
          if (Array.isArray(reldata)) {
            reldata = reldata.map(rec =>
              (rec instanceof rel.model) ? rec : rel.model.create(rec, this));
          } else {
            reldata = rel.model.create(reldata, this);
          }
        } else {
          this.logwarn(`${this._actualClass.name}: Relations ${relkey} doesn't have a model. It will be used as plain data`);
        }
      }
      //add
      this[relkey] = reldata;
    }

    if (resetOrig) {
      this._orig = {};
      for (let field of this._fieldsAndRelations) {
        this._orig[field] = this[field];
      }
    }
  }


  /**
   * Return all fields with current data
   * No deepcopy
   */
  public get _data(): hash<any> {
    let res = {};
    for (let field of this._fieldsAndRelations) {
      res[field] = this[field];
    }
    if (this._ownerfield) {
      res[this._ownerfield] = this[this._ownerfield];
    }
    return res;
  }


  /**
   * Maak een volledig recursieve dataset
   */
  savedata(): hash<any> {
    let data = this._data;
    for (let relkey of this._relationFields) {
      let rel = this.relationDetails(relkey);
      if ((relkey in data) && (data[relkey]) && rel.model) {
        //omzetten naar platte data
        if (Array.isArray(data[relkey]))
        {
          data[relkey] = data[relkey].map(sub => (sub instanceof rel.model) ? sub.savedata() : sub);
        }
        else if (data[relkey] instanceof rel.model) {
          data[relkey] =data[relkey].savedata();
        }
      }
      //als we geen model weten is het al platte data
    }
    return data;
  }

  /**
   * Returns one _orig field or all _orig data
   * No deepcopy
   * @param key
   */
  public orig(key?: string): any {
    if (key) {
      if (this._fieldsAndRelations.includes(key)) {
        return this._orig[key];
      } else {
        return null;
      }
    }
    //everythinh:
    let res = {};
    for (let field of this._fieldsAndRelations) {
      res[field] = this._orig[field];
    }
    return res;
  }

  /**
   * Clones the model and it's related models as if it's a new model. Watch out:
   * - Fields will be copied (shallow), not cloned
   * - We do not set the owner of the top element, we do set it for sub model instances
   * - Orig will be the default values from the constructor
   */
  cloneAsNew<Modelinstance extends Basemodel>(this: Modelinstance): Modelinstance {
    let inst = new this._actualClass as Modelinstance;
    //bouw orig op
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = inst[field];
    }

    for (let field of this._fields) {
      inst[field] = this[field];
    }
    //new: id is dus weg
    inst.id = null;
    //relaties
    for (let relkey of Object.keys(this._relations)) {
      let rel = this.relationDetails(relkey);
      if (Array.isArray(this[relkey])) {
        inst[relkey] = this[relkey].map(el => el.cloneAsNew());
        for (let el of inst[relkey]) {
          el._owner = inst;
        }
      } else if (this[relkey] instanceof rel.model) {
        inst[relkey] = this[relkey].cloneAsNew();
        inst[relkey]._owner = inst;
      } else {
        //copy, probably of undefined
        inst[relkey] = this[relkey];
      }
    }
    return inst;
  }

  /**
   * Create a full clone of the instance: data, orig and owner.
   */
  fullClone<Modelinstance extends Basemodel>(this: Modelinstance): Modelinstance {
    let inst = new this._actualClass as Modelinstance;
    //copy orig
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = this._orig[field];
    }

    for (let field of this._fields) {
      inst[field] = this[field]; //shallow!
    }

    inst._owner = this._owner;

    //relations
    for (let relkey of Object.keys(this._relations)) {
      let rel = this.relationDetails(relkey);
      if (Array.isArray(this[relkey])) {
        inst[relkey] = this[relkey].map(el => el.fullClone());
        for (let el of inst[relkey]) {
          el._owner = inst;
        }
      } else if (this[relkey] instanceof rel.model) {
        inst[relkey] = this[relkey].fullClone();
        inst[relkey]._owner = inst;
      } else {
        //shallow copy, probably of undefined
        inst[relkey] = this[relkey];
      }
    }

    //tags, these are just a deep clone
    inst['_tags'] = cloneDeep(this._tags);

    return inst;
  }

  ///////// helpers ///////////////
  /**
   * Is dit een nieuw, niet opgeslagen record?
   */
  public isNieuw(): boolean {
    return !this.id;
  }


  /**
   * Geeft een vaste tracker-id voor in ngFor of names van controls (gebruik GEEN index uit ngFor for names,dat gaat mis bij splice etc)
   */
  public get trackerId(): string {
    if (!this._trackerid) {
      this._trackerid = `${Basemodel.trackerIdSerial++}`; //goed genoeg, vind ik
    }
    return this._trackerid;
  }


  /**
   * Semi-abstract. Te gebruiken voor titles etc
   */
  public get recordbeschrijving(): string {
    return '';
  }

}
